import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import { hrApi } from '@/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Input } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Skeleton';
import { TablePagination } from '@/components/ui/TablePagination';
import { ViewModeToggle, LIST_HIERARCHY_TOGGLE_OPTIONS } from '@/components/ui/ViewModeToggle';
import { HrEmployeeDetailPanel } from '@/modules/hr/components/HrEmployeeDetailPanel';
import { employeeInitials } from '@/modules/hr/components/HrEmployeeProfileHeader';
import { HrHierarchyTree } from '@/modules/hr/components/HrHierarchyTree';
import type {
  Employee360Response,
  HrEmployeeWorkforceDashboard,
  HrEmploymentHistory,
  HrHierarchyNode,
  HrLifecycleEvent,
} from '@/types/hr';
import type { EmployeeResponse } from '@/types/employee';
import { useDebounce } from '@/hooks/useDebounce';
import { usePagination } from '@/hooks/usePagination';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
import { cn } from '@/utils/cn';

export function HrEmployee360Page() {
  const navigate = useNavigate();
  const { employeeId: routeEmployeeId } = useParams<{ employeeId: string }>();

  const pagination = usePagination(25);
  const debouncedSearch = useDebounce(pagination.search, 300);

  const [sidebarMode, setSidebarMode] = useState<'list' | 'hierarchy'>('list');
  const [employees, setEmployees] = useState<EmployeeResponse[]>([]);
  const [hierarchy, setHierarchy] = useState<HrHierarchyNode[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [treeLoading, setTreeLoading] = useState(false);

  const [selectedId, setSelectedId] = useState(routeEmployeeId ?? '');
  const [view, setView] = useState<Employee360Response | null>(null);
  const [workforce, setWorkforce] = useState<HrEmployeeWorkforceDashboard | null>(null);
  const [lifecycle, setLifecycle] = useState<HrLifecycleEvent[]>([]);
  const [employmentHistory, setEmploymentHistory] = useState<HrEmploymentHistory[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const [eventType, setEventType] = useState('CONFIRMED');
  const [effectiveDate, setEffectiveDate] = useState('');

  useEffect(() => {
    if (routeEmployeeId) {
      setSelectedId(routeEmployeeId);
    }
  }, [routeEmployeeId]);

  useEffect(() => {
    pagination.resetPage();
  }, [debouncedSearch, pagination.resetPage]);

  const loadEmployees = useCallback(async () => {
    setListLoading(true);
    try {
      const res = await hrApi.listVisibleEmployees({
        search: debouncedSearch.trim() || undefined,
        page: pagination.page,
        size: pagination.size,
        activeOnly: true,
        sortBy: 'firstName',
        sortDirection: 'ASC',
      });
      setEmployees(res.content);
      pagination.applyPageResponse(res);
    } catch (err) {
      showApiError(err, 'Failed to load employees');
    } finally {
      setListLoading(false);
    }
  }, [debouncedSearch, pagination.page, pagination.size, pagination.applyPageResponse]);

  const loadHierarchy = useCallback(async () => {
    setTreeLoading(true);
    try {
      setHierarchy(await hrApi.hierarchyTree());
    } catch (err) {
      showApiError(err, 'Failed to load hierarchy');
    } finally {
      setTreeLoading(false);
    }
  }, []);

  useEffect(() => {
    if (sidebarMode === 'list') {
      void loadEmployees();
    }
  }, [sidebarMode, loadEmployees]);

  useEffect(() => {
    if (sidebarMode === 'hierarchy') {
      void loadHierarchy();
    }
  }, [sidebarMode, loadHierarchy]);

  const selectEmployee = useCallback(
    (id: number) => {
      const idStr = String(id);
      setSelectedId(idStr);
      navigate(`/hr/employees/${id}`, { replace: true });
    },
    [navigate],
  );

  const loadDetail = useCallback(async (id: number) => {
    setDetailLoading(true);
    try {
      const [v, h, w, eh] = await Promise.all([
        hrApi.employee360(id),
        hrApi.lifecycleHistory(id),
        hrApi.employeeWorkforceDashboard(id),
        hrApi.employmentHistory(id),
      ]);
      setView(v);
      setLifecycle(h);
      setWorkforce(w);
      setEmploymentHistory(eh);
    } catch (err) {
      showApiError(err, 'Failed to load employee details');
      setView(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setView(null);
      setWorkforce(null);
      setLifecycle([]);
      setEmploymentHistory([]);
      return;
    }
    void loadDetail(Number(selectedId));
  }, [selectedId, loadDetail]);

  const recordLifecycle = async () => {
    if (!selectedId || !effectiveDate) return;
    try {
      await hrApi.recordLifecycleChange({
        employeeId: Number(selectedId),
        eventType,
        effectiveDate,
      });
      notify.success('Lifecycle event recorded');
      setLifecycle(await hrApi.lifecycleHistory(Number(selectedId)));
    } catch (err) {
      showApiError(err, 'Lifecycle update failed');
    }
  };

  return (
    <div className="page-content">
      <PageHeader
        title="Employee directory"
        description="Browse employees, explore reporting hierarchy, and open detailed HR profiles"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'HR', to: '/hr' }, { label: 'Employees' }]}
      />

      <div className="grid gap-4 lg:grid-cols-[minmax(280px,340px)_minmax(0,1fr)]">
        <aside
          className="card flex max-h-[calc(100vh-6rem)] flex-col overflow-hidden lg:sticky lg:top-4 lg:self-start"
          aria-label="Employee directory navigation"
        >
          <div className="border-b border-slate-200 p-3 dark:border-slate-700">
            <ViewModeToggle
              value={sidebarMode}
              onChange={setSidebarMode}
              options={LIST_HIERARCHY_TOGGLE_OPTIONS}
              className="w-full"
            />
          </div>

          {sidebarMode === 'list' ? (
            <>
              <div className="border-b border-slate-200 p-3 dark:border-slate-700">
                <div className="relative">
                  <SearchIcon
                    className="pointer-events-none absolute left-3 top-[2.35rem] h-4 w-4 text-slate-400"
                    fontSize="inherit"
                    aria-hidden
                  />
                  <Input
                    label="Search employees"
                    value={pagination.search}
                    onChange={(e) => {
                      pagination.setSearch(e.target.value);
                      pagination.resetPage();
                    }}
                    placeholder="Name, code, or email"
                    className="pl-9"
                    aria-describedby="employee-list-summary"
                  />
                </div>
                <p id="employee-list-summary" className="mt-2 text-xs text-slate-500" aria-live="polite">
                  {listLoading
                    ? 'Loading employees…'
                    : pagination.totalElements > 0
                      ? `${pagination.totalElements} employee${pagination.totalElements === 1 ? '' : 's'} found`
                      : 'No employees match your search'}
                </p>
              </div>

              <div
                className="min-h-0 flex-1 overflow-y-auto p-2"
                role="listbox"
                aria-label="Employees"
                aria-busy={listLoading}
              >
                {listLoading &&
                  Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="mb-1 flex items-center gap-3 rounded-lg px-3 py-2.5">
                      <Skeleton className="h-9 w-9 shrink-0 rounded-full" />
                      <div className="flex-1 space-y-1.5">
                        <Skeleton className="h-4 w-3/4" />
                        <Skeleton className="h-3 w-1/2" />
                      </div>
                    </div>
                  ))}

                {!listLoading && employees.length === 0 && (
                  <p className="px-2 py-4 text-center text-sm text-slate-500">Try a different search term.</p>
                )}

                {!listLoading &&
                  employees.map((emp) => {
                    const selected = String(emp.id) === selectedId;
                    const initials = employeeInitials(emp.firstName, emp.lastName);
                    const label = `${emp.firstName} ${emp.lastName ?? ''}, ${emp.employeeCode}`;
                    return (
                      <button
                        key={emp.id}
                        type="button"
                        role="option"
                        aria-selected={selected}
                        aria-current={selected ? 'true' : undefined}
                        aria-label={label}
                        onClick={() => selectEmployee(emp.id)}
                        className={cn(
                          'mb-1 flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40',
                          selected
                            ? 'bg-primary-50 text-primary-900 ring-1 ring-primary-200 dark:bg-primary-950/50 dark:ring-primary-800'
                            : 'hover:bg-slate-50 dark:hover:bg-slate-800/60',
                        )}
                      >
                        <span
                          className={cn(
                            'flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                            selected
                              ? 'bg-primary-600 text-white'
                              : 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
                          )}
                          aria-hidden
                        >
                          {initials}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-medium">
                            {emp.firstName} {emp.lastName ?? ''}
                          </span>
                          <span className="block truncate text-xs text-slate-500">
                            {emp.employeeCode}
                            {emp.designation ? ` · ${emp.designation}` : ''}
                          </span>
                        </span>
                      </button>
                    );
                  })}
              </div>

              <TablePagination
                page={pagination.page}
                totalPages={pagination.totalPages}
                totalElements={pagination.totalElements}
                onPageChange={pagination.setPage}
                loading={listLoading}
                hasRows={employees.length > 0}
              />
            </>
          ) : (
            <div className="min-h-0 flex-1 overflow-y-auto p-2" aria-busy={treeLoading}>
              {treeLoading && (
                <div className="space-y-2 p-2" aria-label="Loading hierarchy">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} className="h-8 w-full" />
                  ))}
                </div>
              )}
              {!treeLoading && (
                <HrHierarchyTree
                  nodes={hierarchy}
                  selectedId={selectedId ? Number(selectedId) : undefined}
                  onSelect={selectEmployee}
                  defaultExpanded
                />
              )}
            </div>
          )}
        </aside>

        <main className="min-w-0" aria-label="Employee profile details">
          <HrEmployeeDetailPanel
            view={view}
            workforce={workforce}
            lifecycle={lifecycle}
            employmentHistory={employmentHistory}
            loading={detailLoading}
            eventType={eventType}
            effectiveDate={effectiveDate}
            onEventTypeChange={setEventType}
            onEffectiveDateChange={setEffectiveDate}
            onRecordLifecycle={() => void recordLifecycle()}
            onEmploymentSaved={() => {
              if (selectedId) void loadDetail(Number(selectedId));
            }}
          />
        </main>
      </div>
    </div>
  );
}
